import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { translatePlanText, translatePlanMaxTech } from "@/lib/planTranslate";
import {
  Cable, Zap, ArrowRight, PlayCircle, CheckCircle2, Warehouse, FileSpreadsheet, Receipt,
  ClipboardSignature, Route, FolderOpen, Smartphone, Shield, Timer, ChevronDown, Menu, X,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function toYouTubeEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (u.pathname.startsWith("/embed/")) return url;
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    return null;
  } catch { return null; }
}
function toLoomEmbed(url) { return (url || "").includes("loom.com") ? url.replace("/share/", "/embed/") : null; }
function toVimeoEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("vimeo.com")) return null;
    if (u.pathname.startsWith("/video/")) return url;
    const id = u.pathname.split("/").filter(Boolean).pop();
    return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
  } catch { return null; }
}
const toEmbedUrl = (url) => toYouTubeEmbed(url) || toLoomEmbed(url) || toVimeoEmbed(url);

const featureIcons = [Warehouse, FileSpreadsheet, Receipt, ClipboardSignature, Route, FolderOpen];

export default function MarketingLandingPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [landing, setLanding] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/plans`).then((r) => setPlans(r.data)).catch(() => {});
    axios.get(`${BACKEND}/api/public/landing-config`).then((r) => setLanding(r.data)).catch(() => {});
  }, []);

  const goSignup = () => nav("/signup");
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileNav(false);
  };

  const embed = toEmbedUrl(landing.video_url);
  const features = ["wh", "ass", "bef", "bolle", "route", "docs"];
  const faq = [1, 2, 3, 4, 5, 6];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top announcement bar */}
      <div className="bg-gradient-to-r from-[#0052FF] via-blue-600 to-indigo-600 py-2 text-center text-xs font-medium text-white">
        {t("mkt.top_bar_note")}
      </div>

      {/* Sticky header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md" data-testid="mkt-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-sm bg-[#0052FF] text-white"><Cable className="h-5 w-5" /></div>
            <span className="text-lg font-bold tracking-tight">EVOSOFT</span>
          </Link>
          <nav className="hidden items-center gap-7 text-sm font-medium text-slate-600 md:flex">
            <button onClick={() => scrollTo("features")} className="hover:text-slate-900">{t("mkt.nav_features")}</button>
            <button onClick={() => scrollTo("how")} className="hover:text-slate-900">{t("mkt.nav_how")}</button>
            <button onClick={() => scrollTo("pricing")} className="hover:text-slate-900">{t("mkt.nav_pricing")}</button>
            <button onClick={() => scrollTo("faq")} className="hover:text-slate-900">{t("mkt.nav_faq")}</button>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher compact />
            <Link to="/login" className="hidden text-sm font-medium text-slate-600 hover:text-slate-900 md:block">{t("mkt.nav_login")}</Link>
            <Button onClick={goSignup} className="hidden rounded-sm bg-[#0052FF] text-white hover:bg-[#0040CC] md:inline-flex" data-testid="mkt-cta-top">
              {t("mkt.cta_primary")} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <button onClick={() => setMobileNav(!mobileNav)} className="md:hidden" aria-label="menu">
              {mobileNav ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {mobileNav && (
          <div className="border-t border-slate-200 bg-white px-6 py-4 md:hidden">
            {["features","how","pricing","faq"].map((k) => (
              <button key={k} onClick={() => scrollTo(k)} className="block w-full py-2 text-left text-sm font-medium text-slate-700">{t(`mkt.nav_${k === "how" ? "how" : k}`)}</button>
            ))}
            <Link to="/login" className="block py-2 text-sm font-medium text-slate-700">{t("mkt.nav_login")}</Link>
            <Button onClick={goSignup} className="mt-3 w-full rounded-sm bg-[#0052FF] text-white hover:bg-[#0040CC]">{t("mkt.cta_primary")}</Button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-blue-50 via-white to-white" />
        <div className="absolute -top-32 -right-40 -z-10 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-40 -z-10 h-96 w-96 rounded-full bg-indigo-200/40 blur-3xl" />

        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#0052FF]">
                <Zap className="h-3 w-3" /> {t("mkt.hero_kicker")}
              </div>
              <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                <Trans i18nKey="mkt.hero_title" components={[<br key="0" />, <span key="1" className="text-[#0052FF]" />]} />
              </h1>
              <p className="mt-6 text-lg text-slate-600" dangerouslySetInnerHTML={{ __html: t("mkt.hero_subtitle") }} />
              <div className="mt-8 flex flex-wrap gap-3">
                <Button onClick={goSignup} size="lg" className="rounded-sm bg-[#0052FF] text-base hover:bg-[#0040CC]" data-testid="mkt-cta-hero">
                  {t("mkt.cta_primary")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                {embed && (
                  <Button variant="outline" size="lg" onClick={() => scrollTo("video")} className="rounded-sm border-slate-300 text-base">
                    <PlayCircle className="mr-2 h-4 w-4" /> {t("mkt.cta_secondary")}
                  </Button>
                )}
              </div>
              <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
                {[1,2,3].map((n) => (
                  <li key={n} className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {t(`mkt.hero_bullet_${n}`)}</li>
                ))}
              </ul>
            </div>

            {/* Hero visual: app mockup */}
            <div className="relative" data-testid="hero-mockup">
              <div className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-2xl">
                <div className="flex items-center gap-1.5 border-b border-slate-800 bg-slate-800 px-3 py-2">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  <div className="h-2 w-2 rounded-full bg-yellow-500" />
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <div className="ml-3 truncate rounded-sm bg-slate-700 px-2 py-0.5 text-[10px] text-slate-300">webevosoft.com/dashboard</div>
                </div>
                <div className="grid grid-cols-3 gap-3 bg-white p-4 text-slate-900">
                  {[
                    { label: "Seriali", value: "1,284", color: "bg-blue-100 text-blue-700" },
                    { label: "Impianti oggi", value: "37", color: "bg-emerald-100 text-emerald-700" },
                    { label: "KPI", value: "94%", color: "bg-indigo-100 text-indigo-700" },
                  ].map((c, i) => (
                    <div key={i} className={`rounded-sm p-3 ${c.color}`}>
                      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{c.label}</div>
                      <div className="mt-1 text-xl font-bold">{c.value}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-4 gap-2 bg-slate-50 p-4">
                  {[Warehouse, FileSpreadsheet, Receipt, ClipboardSignature].map((Ic, i) => (
                    <div key={i} className="flex flex-col items-center rounded-sm border border-slate-200 bg-white p-3">
                      <Ic className="h-5 w-5 text-[#0052FF]" />
                      <div className="mt-1 h-1.5 w-10 rounded-full bg-slate-200" />
                    </div>
                  ))}
                </div>
              </div>
              {/* Mobile mockup peeking */}
              <div className="absolute -bottom-6 -right-4 hidden w-40 rotate-6 overflow-hidden rounded-2xl border-4 border-slate-800 bg-white shadow-xl lg:block">
                <div className="bg-slate-800 py-1 text-center text-[10px] text-white">📱 Tecnico</div>
                <div className="space-y-2 p-2">
                  <div className="rounded-sm bg-blue-50 p-2">
                    <div className="text-[10px] font-bold text-blue-900">Via Roma 12</div>
                    <div className="text-[8px] text-blue-700">09:30 · FTTH</div>
                  </div>
                  <div className="rounded-sm bg-slate-100 p-2">
                    <div className="text-[10px] font-bold">Via Milano 4</div>
                    <div className="text-[8px] text-slate-500">10:45 · FTTC</div>
                  </div>
                  <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-2 text-center text-[9px] font-bold text-emerald-800">Scansiona seriale</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-6 py-10 md:grid-cols-4">
          {[1,2,3,4].map((n) => (
            <div key={n} className="text-center">
              <div className="text-3xl font-bold text-[#0052FF]">{t(`mkt.stat_${n}_num`)}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">{t(`mkt.stat_${n}_lbl`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Video demo */}
      {embed && (
        <section id="video" className="mx-auto max-w-5xl px-6 py-16">
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{landing.video_title || t("mkt.video_placeholder")}</div>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl">{t("mkt.watch_full_demo")}</h2>
          </div>
          <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-black shadow-2xl" style={{ paddingTop: "56.25%", position: "relative" }}>
            <iframe src={embed} title="demo" className="absolute inset-0 h-full w-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
          </div>
          {landing.video_description && <p className="mt-4 text-center text-sm text-slate-500">{landing.video_description}</p>}
        </section>
      )}

      {/* HOW IT WORKS */}
      <section id="how" className="mx-auto max-w-7xl px-6 py-20">
        <div className="text-center">
          <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">{t("mkt.how_kicker")}</div>
          <h2 className="mt-2 text-3xl font-bold sm:text-4xl lg:text-5xl">{t("mkt.how_title")}</h2>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
          {[1,2,3].map((n) => (
            <div key={n} className="relative rounded-2xl border border-slate-200 bg-white p-8 transition hover:border-[#0052FF] hover:shadow-lg" data-testid={`how-step-${n}`}>
              <div className="absolute -top-4 left-8 grid h-8 w-8 place-items-center rounded-full bg-[#0052FF] text-sm font-bold text-white">{n}</div>
              <h3 className="mt-4 text-lg font-bold">{t(`mkt.how_step_${n}_title`)}</h3>
              <p className="mt-2 text-sm text-slate-600">{t(`mkt.how_step_${n}_desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-y border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">{t("mkt.features_kicker")}</div>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl lg:text-5xl">{t("mkt.features_title")}</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((key, i) => {
              const Icon = featureIcons[i];
              return (
                <div key={key} className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-[#0052FF] hover:shadow-xl" data-testid={`feat-${key}`}>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-blue-50 text-[#0052FF] transition group-hover:bg-[#0052FF] group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-base font-bold">{t(`mkt.feat_${key}_title`)}</h3>
                  <p className="mt-2 text-sm text-slate-600">{t(`mkt.feat_${key}_desc`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MOBILE SECTION */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">{t("mkt.mobile_kicker")}</div>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl">{t("mkt.mobile_title")}</h2>
            <p className="mt-4 text-slate-600">{t("mkt.mobile_desc")}</p>
            <ul className="mt-6 space-y-2 text-sm">
              {["b1","b2","b3"].map((k) => (
                <li key={k} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" /> {t(`mkt.mobile_${k}`)}</li>
              ))}
            </ul>
          </div>
          <div className="order-1 flex justify-center lg:order-2">
            <div className="relative">
              <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 blur-3xl" />
              <div className="w-56 overflow-hidden rounded-[2.2rem] border-8 border-slate-900 bg-white shadow-2xl">
                <div className="bg-slate-900 py-1 text-center"><div className="mx-auto h-1 w-14 rounded-full bg-slate-700" /></div>
                <div className="space-y-3 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Miei appuntamenti</div>
                  {[
                    { time: "09:30", addr: "Via Roma 12", type: "FTTH", color: "bg-blue-50 border-blue-200" },
                    { time: "10:45", addr: "Via Milano 4", type: "FTTC", color: "bg-slate-50 border-slate-200" },
                    { time: "13:00", addr: "Via Napoli 8", type: "FTTH", color: "bg-slate-50 border-slate-200" },
                  ].map((a, i) => (
                    <div key={i} className={`rounded-sm border p-2 ${a.color}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-bold">{a.time}</div>
                        <div className="rounded-sm bg-slate-900 px-1.5 py-0.5 text-[9px] font-bold text-white">{a.type}</div>
                      </div>
                      <div className="mt-1 text-[11px] text-slate-700">{a.addr}</div>
                    </div>
                  ))}
                  <button className="w-full rounded-sm bg-[#0052FF] py-2 text-[11px] font-bold text-white">📷 Scansiona seriale</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">{t("mkt.pricing_kicker")}</div>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl lg:text-5xl">{t("mkt.pricing_title")}</h2>
            <p className="mt-4 text-slate-600">{t("mkt.pricing_subtitle")}</p>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {plans.map((p) => (
              <div key={p.id} className={`relative rounded-2xl border-2 bg-white p-8 transition ${p.code === "pro" ? "border-[#0052FF] shadow-xl md:-mt-4 md:pb-12" : "border-slate-200"}`} data-testid={`mkt-plan-${p.code}`}>
                {p.code === "pro" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#0052FF] px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-white">{t("landing.most_chosen")}</div>
                )}
                <div className="text-lg font-bold">{p.name}</div>
                {p.description && <div className="mt-1 text-sm text-slate-500">{translatePlanText(p.description, t)}</div>}
                <div className="mt-4 text-5xl font-bold text-[#0052FF]">€{Number(p.price_eur).toFixed(0)}<span className="text-base font-normal text-slate-500">{t("landing.per_month")}</span></div>
                {p.max_technicians && <div className="mt-1 text-xs text-slate-500">{translatePlanMaxTech(p.max_technicians, t)}</div>}
                {(p.features || []).length > 0 && (
                  <ul className="mt-5 space-y-2 text-sm text-slate-700">
                    {p.features.map((f, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" /> {translatePlanText(f, t)}</li>)}
                  </ul>
                )}
                <Button onClick={goSignup} className={`mt-6 w-full rounded-sm ${p.code === "pro" ? "bg-[#0052FF] hover:bg-[#0040CC]" : "bg-slate-900 hover:bg-slate-800"}`}>
                  {t("landing.activate_free_7")}
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/signup" className="text-sm font-semibold text-[#0052FF] hover:underline">{t("mkt.pricing_all_plans")}</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-4xl px-6 py-20">
        <div className="text-center">
          <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">{t("mkt.faq_kicker")}</div>
          <h2 className="mt-2 text-3xl font-bold sm:text-4xl">{t("mkt.faq_title")}</h2>
        </div>
        <div className="mt-10 space-y-3">
          {faq.map((n) => (
            <div key={n} className="overflow-hidden rounded-sm border border-slate-200 bg-white">
              <button
                onClick={() => setOpenFaq(openFaq === n ? null : n)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-medium hover:bg-slate-50"
                data-testid={`faq-toggle-${n}`}
              >
                <span>{t(`mkt.faq_q${n}`)}</span>
                <ChevronDown className={`h-4 w-4 flex-shrink-0 text-slate-400 transition ${openFaq === n ? "rotate-180" : ""}`} />
              </button>
              {openFaq === n && (
                <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 text-sm text-slate-600">
                  {t(`mkt.faq_a${n}`)}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0052FF] via-blue-600 to-indigo-700" />
        <div className="mx-auto max-w-4xl px-6 py-20 text-center text-white">
          <h2 className="text-3xl font-bold sm:text-4xl lg:text-5xl">{t("mkt.final_cta_title")}</h2>
          <p className="mt-4 text-blue-100 text-lg">{t("mkt.final_cta_subtitle")}</p>
          <Button onClick={goSignup} size="lg" className="mt-8 rounded-sm bg-white text-[#0052FF] text-base hover:bg-blue-50" data-testid="mkt-cta-final">
            {t("mkt.cta_primary")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1 text-xs text-blue-100">
            {[1,2,3].map((n) => (
              <span key={n} className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {t(`mkt.hero_bullet_${n}`)}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-900 py-12 text-slate-400">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-6 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-sm bg-[#0052FF] text-white"><Cable className="h-4 w-4" /></div>
              <span className="text-lg font-bold text-white">EVOSOFT</span>
            </div>
            <p className="mt-3 text-xs text-slate-400">{t("mkt.footer_tagline")}</p>
            <div className="mt-4"><LanguageSwitcher compact /></div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white">{t("mkt.footer_product")}</div>
            <ul className="mt-3 space-y-2 text-xs">
              <li><button onClick={() => scrollTo("features")} className="hover:text-white">{t("mkt.nav_features")}</button></li>
              <li><button onClick={() => scrollTo("pricing")} className="hover:text-white">{t("mkt.nav_pricing")}</button></li>
              <li><Link to="/signup" className="hover:text-white">{t("mkt.cta_primary")}</Link></li>
              <li><Link to="/login" className="hover:text-white">{t("mkt.nav_login")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white">{t("mkt.footer_company")}</div>
            <ul className="mt-3 space-y-2 text-xs">
              <li><button onClick={() => scrollTo("how")} className="hover:text-white">{t("mkt.nav_how")}</button></li>
              <li><button onClick={() => scrollTo("faq")} className="hover:text-white">{t("mkt.nav_faq")}</button></li>
              <li><a href="mailto:info@webevosoft.com" className="hover:text-white">{t("mkt.footer_contact")}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white">{t("mkt.footer_legal")}</div>
            <ul className="mt-3 space-y-2 text-xs">
              <li><a href="/privacy" className="hover:text-white">{t("mkt.footer_privacy")}</a></li>
              <li><a href="/terms" className="hover:text-white">{t("mkt.footer_terms")}</a></li>
            </ul>
          </div>
        </div>
        <div className="mx-auto mt-10 max-w-7xl border-t border-slate-800 px-6 pt-6 text-center text-xs">{t("mkt.footer_copy")}</div>
      </footer>
    </div>
  );
}
